'use client'

import type { Locale } from '@/i18n/config'
import { resolvePathname } from '@/i18n/localized-pathname'
import { Link } from '@/i18n/routing'
import { useLocale } from 'next-intl'
import { usePathname } from 'next/navigation'

import classNames from 'classnames'
import type { ComponentProps } from 'react'

import { useBreadcrumbsStore } from '@/modules/breadcrumbs/hooks/use-breadcrumbs'
import Card from './Card'

import { IconPlus } from '@tabler/icons-react'
const CUSTOM_ROUTES = ['leistungen', 'projekte', 'news', 'agentur', 'lp']
// Segmente, deren Beschriftung capitalize falsch schreiben wuerde ("Wo Wir Helfen")
const SEGMENT_LABELS: Record<string, string> = { 'wo-wir-helfen': 'Wo wir helfen', 'how-we-help': 'How we help' }
export const Breadcrumbs = () => {
  const { currentRouteName } = useBreadcrumbsStore()
  const locale = useLocale() as Locale
  // Nicht direkt aus usePathname ablesen: beim Vorrendern steht dort der interne
  // Pfad (/en/kostenlose-beratung), im Browser der uebersetzte. Der sichtbare Text
  // wich ab (React #418) und englische Seiten zeigten kurz deutsche Breadcrumbs.
  // Text kommt jetzt aus dem uebersetzten Pfad, Logik und Links aus dem internen.
  const { internal, localized } = resolvePathname(usePathname(), locale)
  const internalSegments = internal.split('/').filter(Boolean)
  const crumbs = localized
    .split('/')
    .filter(Boolean)
    .map((segment, i) => ({ segment, internalSegment: internalSegments[i] ?? segment, href: '/' + internalSegments.slice(0, i + 1).join('/') }))
    .filter((crumb) => crumb.internalSegment !== 'lp')
  const segments = crumbs.map((crumb) => crumb.internalSegment)
  return (
    <Card className="hidden lg:flex flex-row! items-center p-8 gap-x-6 border border-accent rounded-3xl w-max">
      <IconPlus size={20} className="text-accent" />
      <ul className="flex flex-row items-center">
        {crumbs.map(({ segment, href }, index) => {
          // href ist der DE-Pfad-Key ohne Locale-Präfix: der i18n-Link mappt ihn
          // automatisch auf die lokalisierte URL (z.B. /leistungen -> /en/services)
          const isLast = index === crumbs.length - 1

          const label = SEGMENT_LABELS[segment]
          let formatted = label ?? decodeURIComponent(segment.replace(/-/g, ' '))
          if (index === segments.length - 1 && currentRouteName) {
            if (segments.some((r) => CUSTOM_ROUTES.includes(r)) && !CUSTOM_ROUTES.includes(segments[segments.length - 1])) {
              formatted = currentRouteName
            }
          }
          return isLast ? (
            <li key={index} className={classNames('flex flex-row items-center text-white text-lg leading-none', { ['capitalize']: formatted !== currentRouteName && !label })}>
              {formatted}
            </li>
          ) : (
            <li key={index} className="flex flex-row items-center">
              <Link href={href as ComponentProps<typeof Link>['href']} className={classNames('font-medium text-white text-lg leading-none capitalize')}>
                {formatted}
              </Link>
              <span className="mx-1">-</span>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
